import { adValues } from '@tiger/ads';
import { encryptLoginPassword } from '@tiger/api-signature';
import { useMount } from '@tiger/hooks';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { FormControl, FormLabel } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from 'components/base/Button';
import FormGroup from 'components/FormGroup';
import Password from 'components/forms/Password';
import TelcodesPhone from 'components/forms/TelcodesPhone';
import OpenAddition from 'components/OpenAddition';
import Toast from 'components/Toast';
import security from 'stores/security';
import { RegisterTypeEnum } from 'types/common';
import showError from 'utils/form/showFieldError';
import { reportEventList } from 'utils/gtag';
import patterns from 'utils/patterns';
import URL from 'utils/URL';
import withProgressForm, { ProgressFormProps } from 'utils/withProgressForm';
import { handleBackEsop } from '../helper';

// url上的query参数类型
interface QueryData {
    source: 'esop';
    invite?: string;
    tel_code: string;
    username: string;
    esop_token: string;
    redirect: string;
    license: string;
    event?: string;
    next?: string;
    grant_type?: RegisterTypeEnum;
}

const Signup: React.FC<ProgressFormProps> = ({ $formutil }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // 为了避免挨个searchParams.get时遗漏某些不在QueryData中的参数，比如lang等其他额外参数，保持与原功能用法一致，直接转为对象
    const queryData = Object.fromEntries(searchParams.entries()) as unknown as Partial<QueryData>;
    const [loading, setLoading] = useState(false);
    const { $invalid, $params, $batchState, $setValues, $getField } = $formutil;

    useMount(() => {
        const { username = '', tel_code = '86' } = queryData;

        $setValues({
            username,
            tel_code
        });
    });

    const onSubmit = async (ev: React.FormEvent) => {
        ev.preventDefault();

        reportEventList('signup_verification', 'signup');

        if ($invalid) {
            $batchState({
                $dirty: true,
                $touched: true
            });
        } else {
            setLoading(true);

            // 获取url中的参数 只有 phone 注册,增加一个 token,phone
            // esop 特殊来源，使用 esop_token 替代 verifyCode 去 esop 验证 token 的正确性和实效性
            // https://wiki.tigerbrokers.net/pages/viewpage.action?pageId=20611482 wiki@郭江帅
            const { next: nextUrl, esop_token, grant_type = RegisterTypeEnum.PHONE, ...queryParams } = queryData;

            const signupParams: any = {
                ...queryParams,
                ...$params,
                grant_type,
                reg_source: 'esop',
                verify_code: esop_token,
                pwd_api_ver: 'v2',
                login_password: encryptLoginPassword($params.login_password)
            };

            delete signupParams.confirm_password;

            // 如果有邀请码，params 加入 invite_code https://wiki.tigerbrokers.net/pages/viewpage.action?pageId=20611482 @云为国
            if (adValues.invite) {
                signupParams.invite_code = adValues.invite;
            }

            // 如果有adcode，params 加入
            if (adValues.adcode) {
                signupParams.adcode = adValues.adcode;
            }

            try {
                // outh 接口granttype: 'phone' || 'email'
                await security.esopSignup(signupParams, RegisterTypeEnum.PHONE);

                //  如果设置了回调，则注册完成后回调地址
                if (nextUrl && patterns.isSafeUrl(nextUrl)) {
                    if (queryData.event === 'share') {
                        window.location.href = `${nextUrl}#${queryData.event}?invite=${adValues.invite}`;

                        return;
                    }

                    window.location.href = nextUrl;

                    return;
                }

                // 注册后需要获取用户状态信息
                try {
                    await security.getStatus();

                    navigate('/esop/signup');
                } catch (error) {
                    handleBackEsop(__('System Error'), error?.message);
                }
            } catch (error) {
                handleError(error);
            }

            setLoading(false);
        }
    };

    const handleError = error => {
        const verifyCodeFieldUtil = $getField('verify_code');
        let errMsg = error.error_msg;

        switch (Number(error.error_code)) {
            case 11043:
                errMsg = __('This code has expired. Please request a new one');
                showError(verifyCodeFieldUtil, errMsg);
                break;
            case 11044:
                errMsg = __('Invalid code. Please check your code and try again');
                handleBackEsop(__('Registration Failed'), errMsg);
                break;
            default:
                Toast.show(errMsg);
                break;
        }
    };

    return (
        <form className="open-signup-root" noValidate onSubmit={onSubmit}>
            <OpenAddition />
            <TelcodesPhone
                phoneOptions={{
                    name: 'username'
                }}
                label={<FormLabel>{__('Sign up to start a new application')}</FormLabel>}
                disabled={!!queryData.username}
            />
            <Password />
            {/* 没有邀请码链接的手动输入 */}
            {!adValues.invite && (
                <FormGroup
                    name="invite_code"
                    label={__('Invite Code')}
                    checker={value => {
                        if (!value) {
                            return true;
                        }

                        return patterns.isInviteCode(value) || __('The invite code is not valid');
                    }}>
                    <FormControl placeholder={__('Invite Code (If Applicable)')} autoComplete="off" />
                </FormGroup>
            )}
            <Button
                block
                type="primary"
                size="lg"
                htmlType="submit"
                className="btn-submit"
                loading={loading}
                disabled={loading}>
                {__('Sign Up')}
            </Button>

            {queryData.event !== 'share' && (
                <div className="text-meta text-end">
                    {__('Already started ?')}
                    <a href={URL.merge('/login', { query: queryData })}>{__('Log in to continue your application.')}</a>
                </div>
            )}
        </form>
    );
};

export default withProgressForm({
    step: 0,
    basePath: 'esop',
    showProgressBar: false
})(observer(Signup));
